<script setup lang="ts">
import {inject, reactive, ref} from "vue";
import {useRouter} from "vue-router";
import {FilterMatchMode} from "@primevue/core/api";
import {getUsers as apiGetUsers, impersonateUser} from "@/Services/Admin/admin-users";
import Str from "@/Utils/Str";
import {Button, Column, DataTable, MultiSelect, SplitButton} from "@/Components/Library";
import {PageHeading, TableRecordActions} from "@/Components/Global";
import InviteUserDialog from "@/Pages/Admin/Users/InviteUserDialog.vue";
import type dayjs from "dayjs";
import type {AdminIndex} from "@/Types";

type UserLocal = any;

const $date = inject("date") as typeof dayjs;
const router = useRouter();

const state = reactive({
    show: {
        inviteUserDialog: false,
    },
});

const users = ref<AdminIndex<UserLocal>>();

const userMenuItems = [
    {
        label: "Invite User",
        icon: "prime:envelope",
        command: (event) => {
            state.show.inviteUserDialog = true;
        },
    },
    {
        label: "Create Client",
        icon: "prime:user-plus",
        command: (event) => {
            router.push({name: "admin.clients.create"});
        },
    },
];

const filters = ref({
    roles: {
        value: [],
        matchMode: FilterMatchMode.IN,
    },
});

const getUsers = async () => {
    apiGetUsers({
        include: ["roles", "profile", "groupsCount", "contactsCount", "plan"],
        fields: ["roles.name"],
        ...router.currentRoute.value.query,
    }).then((data) => (users.value = data));
};
getUsers();

const impersonate = async (id: number) => {
    await impersonateUser(id);
    window.location.href = "/";
};
</script>
<template>
    <div>
        <div class="m-container">
            <PageHeading title="Users">
                <RouterLink class="ml-auto focus-visible:outline-none" :to="{name: 'admin.users.create'}">
                    <SplitButton
                        rounded
                        class="bg-askr-green text-white transition duration-200 hover:brightness-110"
                        label="Add User"
                        @click="router.push({name: 'admin.users.create'})"
                        :model="userMenuItems"
                        :filters="filters"
                        icon="prime:user-plus"
                    />
                </RouterLink>
            </PageHeading>
        </div>
        <DataTable
            :data="users"
            dataKey="id"
            :loading="false"
            :filters="filters"
            @filter="getUsers"
            :allowTagSearch="false"
            defaultSort="-deleted_at,-created_at"
        >
            <Column expander style="width: 0" />
            <Column field="id" header="ID">
                <template #body="{data}">
                    <RouterLink
                        :to="{
                            name: 'admin.users.show',
                            params: {user: data.id},
                        }"
                        class="hover:underline"
                    >
                        {{ data.id }}
                    </RouterLink>
                </template>
            </Column>
            <Column field="name" header="Name" :sortable="true" sort-field="last_name">
                <template #body="{data}">
                    <RouterLink
                        :to="{
                            name: 'admin.users.show',
                            params: {user: data.id},
                        }"
                        class="hover:underline"
                    >
                        {{ data.name }}
                    </RouterLink>
                </template>
            </Column>
            <Column field="email" header="Email" :sortable="true">
                <template #body="{data}">
                    <a :href="'mailto:' + data.email" class="hover:underline">
                        {{ data.email }}
                    </a>
                </template>
            </Column>
            <!--            <Column field="groups_count" header="Groups" :sortable="true" />-->
            <!--            <Column field="contacts_count" header="Contacts" :sortable="true" />-->
            <Column field="roles.name" header="Roles" :showFilterMatchModes="false" filter-field="roles">
                <template #body="{data}">
                    {{ data.roles ? data.roles.map((r) => Str.of(r).title().value).join(", ") : "-" }}
                </template>
                <template #filter="{filterModel, filterCallback}">
                    <MultiSelect
                        v-model="filterModel.value"
                        @change="filterCallback"
                        :options="['super-admin', 'admin', 'user']"
                        placeholder="Select actions"
                        filter
                        class="p-column-filter"
                        style="min-width: 14rem"
                        :maxSelectedLabels="1"
                    >
                        <template #option="slotProps">
                            <div class="align-items-center flex">
                                <span>{{ Str.of(slotProps.option).capitalise() }}</span>
                            </div>
                        </template>
                    </MultiSelect>
                </template>
                <template #filterclear="{filterCallback}" />
                <template #filterapply="{filterCallback}" />
            </Column>
            <Column field="plan.name" header="Plan" :sortable="true" sort-field="plan_id" />
            <Column field="last_login" header="Logged in">
                <template #body="{data}">
                    {{ data.last_login ? $date(data.last_login.timestamp).format("DD/MM/YYYY HH:mm") : "" }}
                </template>
            </Column>
            <Column field="created_at" header="Created At" :sortable="true">
                <template #body="{data}">
                    {{ $date(data.created_at).format("DD/MM/YYYY HH:mm") }}
                </template>
            </Column>
            <Column field="deleted_at" header="Deleted At" :sortable="true">
                <template #body="{data}">
                    {{ data.deleted_at ? $date(data.deleted_at).format("DD/MM/YYYY HH:mm") : null }}
                </template>
            </Column>
            <Column header="Actions" style="width: 0">
                <template #body="{data}">
                    <div class="flex items-center gap-1">
                        <Button
                            class="bg-black !p-2 text-white"
                            :icon="{
                                icon: 'mdi:spy',
                                width: 16,
                            }"
                            @click="impersonate(data.id)"
                        />
                        <TableRecordActions
                            :name="data.name"
                            :deleted="!!data.deleted_at"
                            :routes="{
                                show: {
                                    name: 'admin.users.show',
                                    params: {user: data.id},
                                },
                                edit: {
                                    name: 'admin.users.edit',
                                    params: {user: data.id},
                                },
                                delete: route('admin.users.destroy', data.id),
                                restore: route('admin.users.restore', data.id),
                            }"
                            @deleted="getUsers"
                        />
                    </div>
                </template>
            </Column>
            <template #expansion="{data}">
                <h2 class="mb-2 text-xl font-bold">{{ data.name }}</h2>
                <table>
                    <tbody>
                        <tr>
                            <td>ID</td>
                            <td>{{ data.id }}</td>
                        </tr>
                        <tr>
                            <td>Email</td>
                            <td>{{ data.email }}</td>
                        </tr>
                        <tr>
                            <td class="align-top">Last login</td>
                            <td>
                                <template v-if="data.last_login">
                                    <ul class="space-y-1">
                                        <li>
                                            <strong>IP:</strong>
                                            {{ data.last_login.ip }}
                                        </li>
                                        <li>
                                            <strong>Browser:</strong>
                                            {{ data.last_login.user_agent }}
                                        </li>
                                        <li>
                                            <strong>Date:</strong>
                                            {{ data.last_login.timestamp }}
                                        </li>
                                    </ul>
                                </template>
                            </td>
                        </tr>
                        <tr>
                            <td>Country</td>
                            <td>{{ data.profile?.country ?? "-" }}</td>
                        </tr>
                        <tr>
                            <td>Groups</td>
                            <td>{{ data.groups_count }}</td>
                        </tr>
                        <tr>
                            <td>Contacts</td>
                            <td>{{ data.contacts_count }}</td>
                        </tr>
                        <tr>
                            <td>Color</td>
                            <td>
                                <div class="h-4 w-4 rounded-full" :class="[`bg-${data.profile.color}`]" />
                            </td>
                        </tr>
                    </tbody>
                </table>
            </template>
        </DataTable>
        <InviteUserDialog v-model:visible="state.show.inviteUserDialog" />
    </div>
</template>
<style scoped>
tr td {
    @apply py-1 pr-2;
}

tr td:first-child {
    @apply font-bold;
}
</style>
